import { CommonModule } from "@angular/common";
import { Clipboard } from "@angular/cdk/clipboard";
import {
  afterNextRender,
  Component,
  DOCUMENT,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { GoogleMapsModule } from "@angular/google-maps";
import { MatButtonModule } from "@angular/material/button";
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { SafeUrl } from "@angular/platform-browser";
import { RouterLink } from "@angular/router";
import { GoogleMapsLoaderService } from "@services/google-maps-loader.service";
import {
  eventShareUrl,
  EventShareChannel,
} from "@services/marketing/event-share-url";
import { ImgFallbackDirective } from "@shared/directives/img-fallback/img-fallback.directive";
import { hasInputChanged } from "@shared/helpers/hasChanges/hasChanges.helper";
import {
  ticketAvailableQuantity,
  ticketIsSoldOut,
} from "@shared/helpers/ticket-availability/ticket-availability.helper";
import { MessageService } from "primeng/api";
import { debounceTime, Subject, takeUntil } from "rxjs";
import { environment } from "../../../../../environments/environment";
import { ButtonComponent } from "../../../../../shared/components/button/button.component";
import { ModalLoginComponent } from "../../../../../shared/components/modal-login/modal-login.component";
import { UtilsService } from "../../../../../utils/utils.service";
import { LocalStorageService } from "../../../../services/local-storage.service";
import { SnackBarService } from "../../../../services/snack-bar.service";
import { EventComboTicket } from "@models/combo-ticket";
import { EventSpace } from "@models/space";
import { ResourceAssignment } from "@models/user";
import {
  Batch,
  EventInfo,
  EventSaleState,
  Ticket,
} from "../../../../services/types/events-management-request.type";
import { EventPolicyComponent } from "../event-policy/event-policy.component";
import {
  HALF_PRICE_RULES,
  type HalfPriceRule,
} from "../../../../../shared/helpers/half-price/half-price.helper";

@Component({
  selector: "app-event-details",
  providers: [MessageService],
  imports: [
    CommonModule,
    FormsModule,
    GoogleMapsModule,
    ButtonComponent,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    EventPolicyComponent,
    ImgFallbackDirective,
    RouterLink,
  ],
  templateUrl: "./event-details.component.html",
  styleUrl: "./event-details.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class EventDetailsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() eventMapCenterLocation: google.maps.LatLngLiteral | null = null;
  @Input() currentBatch: Batch | null = null;
  @Input() isBatchLoading = true;
  @Input() eventInfo: EventInfo | null = null;

  private readonly mapsLoader = inject(GoogleMapsLoaderService);
  private readonly clipboard = inject(Clipboard);

  constructor(
    private utilsService: UtilsService,
    private readonly dialog: MatDialog,
    @Inject(DOCUMENT) private document: Document,
    private localStorageService: LocalStorageService,
    private readonly snackbarService: SnackBarService,
  ) {
    afterNextRender(() => {
      void this.loadGoogleMaps().catch(() => undefined);
    });
  }

  currentUser = JSON.parse(
    this.localStorageService.getItem("current_user") ?? "false",
  );

  eventImage: string | SafeUrl = "";
  eventMapImage: string | SafeUrl = "";

  isAnimatingFavorite = false;
  descriptionExpanded = false;
  spacesExpanded = false;
  private readonly expandedSpaceDescriptions = new Set<number>();
  private readonly expandedHalfPriceRules = new Set<number>();
  private eventMapDialogRef: MatDialogRef<unknown> | null = null;

  private dialogRefLogin: MatDialogRef<ModalLoginComponent> | null = null;

  private favoriteToggle$ = new Subject<{
    eventId: string;
  }>();

  private destroy$ = new Subject<void>();

  isGoogleLoaded = false;

  @Input() spaceIdsInCart: Map<number, number> = new Map();
  @Input() loadingSpaceIds: Set<number> = new Set();
  @Input() isCheckoutInProgress = false;
  @Input() pendingCouponCode: string | null = null;

  @Output() favoriteButtonClick = new EventEmitter();
  @Output() checkoutClick = new EventEmitter<Ticket[]>();
  @Output() addSpaceClick = new EventEmitter<EventSpace>();
  @Output() removeSpaceClick = new EventEmitter<EventSpace>();

  /**
   * Gratuito-privado: o acesso é só por convite e os ingressos são gerados
   * pela organização, então a página não exibe compra de ingressos.
   */
  get isFreePrivate(): boolean {
    return (
      this.eventInfo?.event_type === "free" && !!this.eventInfo?.is_private
    );
  }

  get ticketsSaleState(): EventSaleState | null {
    if (!this.eventInfo || this.isFreePrivate) {
      return null;
    }

    if (
      this.eventInfo.status === "finished" ||
      this.eventInfo.sale_state === "event_passed" ||
      this.eventDateHasPassed(this.eventInfo.end_date)
    ) {
      return "event_passed";
    }

    const apiState = this.eventInfo.sale_state;
    if (
      apiState === "not_started" ||
      apiState === "sold_out" ||
      apiState === "on_sale"
    ) {
      return apiState;
    }

    if (apiState === "none") {
      return null;
    }

    if (this.isBatchLoading) {
      return "on_sale";
    }

    const tickets = this.currentBatch?.tickets ?? [];
    const hasListedTickets = tickets.length > 0;
    const ticketsAllSoldOut =
      hasListedTickets && tickets.every((ticket) => ticketIsSoldOut(ticket));
    const combos = this.comboTickets;
    const hasCombos = combos.length > 0;
    const combosAllSoldOut =
      hasCombos && combos.every((combo) => ticketIsSoldOut(combo));

    if (hasListedTickets || hasCombos) {
      if (
        (!hasListedTickets || ticketsAllSoldOut) &&
        (!hasCombos || combosAllSoldOut)
      ) {
        return "sold_out";
      }
      return "on_sale";
    }

    return "sold_out";
  }

  get showTicketPicker(): boolean {
    return !this.eventInfo?.online_sales_closed && this.ticketsSaleState === "on_sale";
  }

  private salesNotStartedMessage(): string {
    const label = this.salesStartLabel();
    return label
      ? `As vendas começam em ${label}.`
      : "As vendas deste evento ainda não começaram.";
  }

  private salesStartLabel(): string | null {
    const raw = this.eventInfo?.sales_start_at;
    if (!raw) return null;

    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;

    const pad = (value: number) => String(value).padStart(2, "0");
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} às ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  get ticketsSaleBanner(): { title: string; message: string } | null {
    if (this.eventInfo?.online_sales_closed && this.ticketsSaleState !== 'event_passed') {
      return { title: 'Vendas online encerradas', message: 'As compras online encerraram no início do evento. Os ingressos já emitidos continuam válidos.' };
    }
    switch (this.ticketsSaleState) {
      case "event_passed":
        return {
          title: "Este evento já passou",
          message: "As vendas deste evento foram encerradas.",
        };
      case "not_started":
        return {
          title: "Vendas não iniciadas",
          message: this.salesNotStartedMessage(),
        };
      case "sold_out":
        return {
          title: "Ingressos esgotados",
          message: "Não há ingressos disponíveis para este evento.",
        };
      default:
        return null;
    }
  }

  get descriptionIsLong(): boolean {
    return (this.eventInfo?.description?.length ?? 0) > 420;
  }

  toggleDescription(): void {
    this.descriptionExpanded = !this.descriptionExpanded;
  }

  get spacesAreCollapsible(): boolean {
    return (this.eventInfo?.spaces?.length ?? 0) > 2;
  }

  get visibleSpaces(): EventSpace[] {
    const spaces = this.eventInfo?.spaces ?? [];
    return this.spacesExpanded ? spaces : spaces.slice(0, 2);
  }

  toggleSpaces(): void {
    this.spacesExpanded = !this.spacesExpanded;
  }

  isSpaceDescriptionExpanded(space: EventSpace): boolean {
    const id = space.id ?? space.ticket_id;
    return id !== undefined && this.expandedSpaceDescriptions.has(id);
  }

  toggleSpaceDescription(space: EventSpace): void {
    const id = space.id ?? space.ticket_id;
    if (id === undefined) return;

    if (this.expandedSpaceDescriptions.has(id)) {
      this.expandedSpaceDescriptions.delete(id);
    } else {
      this.expandedSpaceDescriptions.add(id);
    }
  }

  openEventMap(preview: TemplateRef<unknown>): void {
    if (!this.eventMapImage) return;

    this.eventMapDialogRef = this.dialog.open(preview, {
      width: "1000px",
      maxWidth: "96vw",
      maxHeight: "94dvh",
      ariaLabel: "Mapa ampliado do evento",
      autoFocus: "first-tabbable",
    });
  }

  spaceTotalPrice(space: EventSpace): number {
    return (
      Number(space.price) +
      (space.absorbs_platform_fee ? 0 : Number(space.tax ?? 0)) +
      Number(space.consumption ?? 0)
    );
  }

  ticketTotalPrice(ticket: Ticket): number {
    return (
      Number(ticket.price) +
      (this.currentBatch?.absorbs_platform_fee ? 0 : Number(ticket.tax ?? 0))
    );
  }

  spaceAvailable(space: EventSpace): boolean {
    const sold = Number(space.sold_quantity ?? 0);
    const reserved = Number(space.reserved_quantity ?? 0);
    return sold === 0 && reserved === 0;
  }

  ticketAvailable(ticket: Ticket): boolean {
    return !ticketIsSoldOut(ticket);
  }

  isHalfPriceTicket(ticket: Ticket): boolean {
    return !!ticket.half_price;
  }

  isHalfPriceRulesExpanded(ticket: Ticket): boolean {
    return this.expandedHalfPriceRules.has(ticket.id);
  }

  toggleHalfPriceRules(ticket: Ticket): void {
    if (this.expandedHalfPriceRules.has(ticket.id)) {
      this.expandedHalfPriceRules.delete(ticket.id);
      return;
    }
    this.expandedHalfPriceRules.add(ticket.id);
  }

  halfPriceRules(ticket: Ticket): HalfPriceRule[] {
    if (ticket.half_price_rules?.length) {
      return ticket.half_price_rules;
    }
    return HALF_PRICE_RULES;
  }

  get comboTickets(): EventComboTicket[] {
    return this.eventInfo?.combo_tickets ?? [];
  }

  get hasComboTickets(): boolean {
    return this.comboTickets.length > 0;
  }

  comboComponentNames(combo: EventComboTicket): string {
    return (combo.component_tickets ?? [])
      .map((ticket) => ticket.title)
      .join(" + ");
  }

  comboIncludedCount(combo: EventComboTicket): number {
    const components = combo.component_tickets ?? [];
    if (!components.length) {
      return Math.max(1, combo.component_ticket_ids?.length ?? 1);
    }
    return components.reduce(
      (total, ticket) => total + Math.max(1, Number(ticket.quantity ?? 1)),
      0,
    );
  }

  comboBadgeLabel(combo: EventComboTicket): string {
    const count = this.comboIncludedCount(combo);
    return `${count} ${count === 1 ? "INGRESSO" : "INGRESSOS"}`;
  }

  comboFormula(combo: EventComboTicket): string {
    const parts = (combo.component_tickets ?? []).map((ticket) => {
      const quantity = Math.max(1, Number(ticket.quantity ?? 1));
      const noun = quantity === 1 ? "ingresso" : "ingressos";
      return `${quantity} ${noun} ${ticket.title}`;
    });
    if (!parts.length && combo.description) {
      return combo.description;
    }
    return parts.length ? `1 combo = ${parts.join(" + ")}` : combo.title;
  }

  comboTogetherCopy(combo: EventComboTicket): string {
    const count = this.comboIncludedCount(combo);
    if (count === 2) {
      return "Os dois ingressos são adicionados juntos à sua compra.";
    }
    return `Os ${count} ingressos são adicionados juntos à sua compra.`;
  }

  comboSameComboCopy(combo: EventComboTicket): string {
    const count = this.comboIncludedCount(combo);
    if (count === 2) {
      return "Os dois ingressos pertencem ao mesmo combo.";
    }
    return `Os ${count} ingressos pertencem ao mesmo combo.`;
  }

  comboTotalPrice(combo: EventComboTicket): number {
    return (
      Number(combo.price) +
      (combo.absorbs_platform_fee ? 0 : Number(combo.tax ?? 0))
    );
  }

  comboAvailable(combo: EventComboTicket): boolean {
    return !ticketIsSoldOut(combo);
  }

  comboRemaining(combo: EventComboTicket): number {
    return ticketAvailableQuantity(combo);
  }

  get selectedTickets(): Ticket[] {
    return (
      this.currentBatch?.tickets?.filter(
        (ticket) => (ticket.addToCartQuantity ?? 0) > 0,
      ) ?? []
    );
  }

  get selectedCombos(): EventComboTicket[] {
    return this.comboTickets.filter(
      (combo) => (combo.addToCartQuantity ?? 0) > 0,
    );
  }

  get selectedSpaces(): EventSpace[] {
    return (
      this.eventInfo?.spaces?.filter((space) => this.spaceInCart(space)) ?? []
    );
  }

  ticketUnitTax(ticket: Ticket): number {
    return this.currentBatch?.absorbs_platform_fee
      ? 0
      : Number(ticket.tax ?? 0);
  }

  comboUnitTax(combo: EventComboTicket): number {
    return combo.absorbs_platform_fee ? 0 : Number(combo.tax ?? 0);
  }

  spaceUnitTax(space: EventSpace): number {
    return space.absorbs_platform_fee ? 0 : Number(space.tax ?? 0);
  }

  get ticketsSubtotal(): number {
    const ticketsTotal = this.selectedTickets.reduce(
      (total, ticket) =>
        total + Number(ticket.addToCartQuantity) * Number(ticket.price),
      0,
    );
    const combosTotal = this.selectedCombos.reduce(
      (total, combo) =>
        total + Number(combo.addToCartQuantity) * Number(combo.price),
      0,
    );
    const spacesTotal = this.selectedSpaces.reduce(
      (total, space) =>
        total + Number(space.price) + Number(space.consumption ?? 0),
      0,
    );
    return ticketsTotal + combosTotal + spacesTotal;
  }

  get taxesSubtotal(): number {
    const ticketsTax = this.selectedTickets.reduce(
      (total, ticket) =>
        total + Number(ticket.addToCartQuantity) * this.ticketUnitTax(ticket),
      0,
    );
    const combosTax = this.selectedCombos.reduce(
      (total, combo) =>
        total + Number(combo.addToCartQuantity) * this.comboUnitTax(combo),
      0,
    );
    const spacesTax = this.selectedSpaces.reduce(
      (total, space) => total + this.spaceUnitTax(space),
      0,
    );
    return ticketsTax + combosTax + spacesTax;
  }

  get includedTicketsCount(): number {
    const ticketsCount = this.selectedTickets.reduce(
      (total, ticket) => total + Number(ticket.addToCartQuantity),
      0,
    );
    const combosCount = this.selectedCombos.reduce(
      (total, combo) =>
        total +
        Number(combo.addToCartQuantity) * this.comboIncludedCount(combo),
      0,
    );
    const spacesCount = this.selectedSpaces.reduce(
      (total, space) => total + Number(space.quantity ?? 1),
      0,
    );
    return ticketsCount + combosCount + spacesCount;
  }

  get comboReceiveCopy(): string | null {
    if (!this.selectedCombos.length) {
      return null;
    }
    const total = this.selectedCombos.reduce(
      (sum, combo) =>
        sum + Number(combo.addToCartQuantity) * this.comboIncludedCount(combo),
      0,
    );
    const noun = total === 1 ? "ingresso" : "ingressos";
    const suffix =
      this.selectedCombos.length === 1 ? "neste combo" : "nos combos";
    return `Você receberá ${total} ${noun} ${suffix}.`;
  }

  ticketRemaining(item: Ticket): number {
    return ticketAvailableQuantity(item);
  }

  spaceInCart(space: EventSpace): boolean {
    return this.spaceIdsInCart.has(Number(space.ticket_id));
  }

  isSpaceLoading(space: EventSpace): boolean {
    return this.loadingSpaceIds.has(Number(space.ticket_id));
  }

  handleAddSpace(space: EventSpace) {
    const userToken = this.localStorageService.getItem("auth-token");
    if (!userToken) {
      this.openModalLogin();
      return;
    }
    this.addSpaceClick.emit(space);
  }

  handleRemoveSpace(space: EventSpace) {
    this.removeSpaceClick.emit(space);
  }

  ngOnDestroy(): void {
    this.eventMapDialogRef?.close();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit() {
    this.subscribeToFavoriteToggle();
  }

  ngOnChanges(changes: SimpleChanges) {
    this.verifyEventInfoChanges(changes);
  }

  openModalLogin() {
    this.dialogRefLogin = this.dialog.open(ModalLoginComponent, {
      width: "500px",
      maxWidth: "95vw",
      maxHeight: "90dvh",
    });

    this.dialogRefLogin.afterClosed().subscribe((userLogged) => {
      if (userLogged) {
        this.checkIfUserIsLoggedIn();
      }
    });
  }

  checkIfUserIsLoggedIn() {
    const userToken = this.localStorageService.getItem("auth-token");

    if (!userToken) {
      this.openModalLogin();
    } else {
      this.adicionarAoCarrinho();
    }
  }

  adicionarAoCarrinho() {
    this.document.documentElement.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth", // Adiciona uma rolagem suave
    });

    const ticketsToBuy =
      this.currentBatch?.tickets?.filter(
        (ticket) => ticket.addToCartQuantity && ticket.addToCartQuantity > 0,
      ) ?? [];

    const combosToBuy = this.comboTickets
      .filter((combo) => (combo.addToCartQuantity ?? 0) > 0 && combo.ticket_id)
      .map(
        (combo) =>
          ({
            id: Number(combo.ticket_id),
            addToCartQuantity: Number(combo.addToCartQuantity),
          }) as Ticket,
      );

    this.checkoutClick.emit([...ticketsToBuy, ...combosToBuy]);
  }

  increaseQuantity(item: Ticket) {
    // Verificar se já atingiu o limite máximo por comprador
    if (item.addToCartQuantity >= item.max_per_buyer) {
      this.snackbarService.openSnackBar(
        `Limite atingido: Máximo de ${item.max_per_buyer} ${item.quantity === 1 ? "ingresso" : "ingressos"} por comprador.`,
        "Entendi",
        "info",
        8000,
      );
      return;
    }

    // Verificar se já atingiu o limite de ingressos disponíveis
    const remaining = this.ticketRemaining(item);
    if (item.addToCartQuantity >= remaining) {
      this.snackbarService.openSnackBar(
        remaining <= 0
          ? "Este ingresso está esgotado."
          : `Apenas ${remaining} ${remaining === 1 ? "ingresso está disponível" : "ingressos estão disponíveis"}.`,
        "Entendi",
        "info",
        8000,
      );
      return;
    }

    // Se não atingiu nenhum limite, incrementa a quantidade
    item.addToCartQuantity++;
  }

  decreaseQuantity(item: Ticket) {
    if (item.addToCartQuantity > 0) {
      item.addToCartQuantity--;
    }
  }

  increaseComboQuantity(combo: EventComboTicket) {
    combo.addToCartQuantity = combo.addToCartQuantity ?? 0;
    const asTicket = {
      addToCartQuantity: combo.addToCartQuantity,
      max_per_buyer: combo.max_per_buyer,
      quantity: combo.quantity,
      available_quantity: combo.available_quantity,
      total_distributed: combo.total_distributed,
      reserved_quantity: combo.reserved_quantity,
    } as Ticket;
    this.increaseQuantity(asTicket);
    combo.addToCartQuantity = asTicket.addToCartQuantity;
  }

  decreaseComboQuantity(combo: EventComboTicket) {
    combo.addToCartQuantity = combo.addToCartQuantity ?? 0;
    if (combo.addToCartQuantity > 0) {
      combo.addToCartQuantity--;
    }
  }

  calcularTotalCompra(): number {
    const ticketsTotal =
      this.currentBatch?.tickets?.reduce((batchTotal, ticket) => {
        return (
          batchTotal + ticket.addToCartQuantity! * this.ticketTotalPrice(ticket)
        );
      }, 0) ?? 0;

    const spacesTotal =
      this.eventInfo?.spaces
        ?.filter((space) => this.spaceInCart(space))
        .reduce((total, space) => total + this.spaceTotalPrice(space), 0) ?? 0;

    const combosTotal =
      this.comboTickets.reduce((total, combo) => {
        return (
          total +
          Number(combo.addToCartQuantity ?? 0) * this.comboTotalPrice(combo)
        );
      }, 0) ?? 0;

    return ticketsTotal + spacesTotal + combosTotal;
  }

  enableFinishSaleButton(): boolean {
    const hasTickets =
      this.currentBatch?.tickets?.some(
        (ticket) => ticket.addToCartQuantity > 0,
      ) ?? false;

    const hasSpaces = this.spaceIdsInCart.size > 0;

    const hasCombos = this.comboTickets.some(
      (combo) => (combo.addToCartQuantity ?? 0) > 0,
    );

    return hasTickets || hasSpaces || hasCombos;
  }

  get finishCheckoutLabel(): string {
    return this.isLoggedUserSellerForEvent()
      ? "FINALIZAR VENDA"
      : "FINALIZAR COMPRA";
  }

  private isLoggedUserSellerForEvent(): boolean {
    if (!this.eventInfo?.id) {
      return false;
    }

    const assignments = this.currentUser?.seller_assignments ?? [];
    return assignments.some((assignment: number | ResourceAssignment) => {
      if (typeof assignment === "number") {
        return assignment === this.eventInfo?.id;
      }

      return (
        assignment.resource_type === "Event" &&
        assignment.resource_id === this.eventInfo?.id
      );
    });
  }

  compartilharWhatsapp(): void {
    const url = this.shareUrl("whatsapp");
    if (!url) return;
    this.document.defaultView?.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(`Confira este evento: ${url}`)}`,
      "_blank",
      "noopener,noreferrer",
    );
  }

  compartilharFacebook(): void {
    const url = this.shareUrl("facebook");
    if (!url) return;
    this.document.defaultView?.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      "_blank",
      "noopener,noreferrer",
    );
  }

  copiarLink(): void {
    const url = this.shareUrl("shared_link");
    if (!url) return;
    const copied = this.clipboard.copy(url);
    this.snackbarService.openSnackBar(
      copied
        ? "Link copiado!"
        : "Não foi possível copiar o link. Tente novamente.",
      "OK",
      copied ? "success" : "error",
    );
  }

  private shareUrl(channel: EventShareChannel): string | undefined {
    if (!this.document.defaultView || !this.eventInfo) return undefined;
    return eventShareUrl(
      this.document.location.href,
      this.eventInfo.id,
      channel,
    );
  }

  compartilharInstagram(): void {
    const url = window.location.href;
    const texto = encodeURIComponent("Confira este evento: ");
    window.open(
      `https://www.instagram.com/?url=${encodeURIComponent(url)}&text=${texto}`,
      "_blank",
    );
  }

  eventDateHasPassed(eventDate: string) {
    return this.utilsService.isPast(eventDate);
  }

  handleFavoriteClick(event: MouseEvent) {
    event.stopPropagation();

    if (!this.eventInfo) {
      return;
    }

    this.favoriteToggle$.next({
      eventId: this.eventInfo.id.toString(),
    });

    this.animateFavoriteIcon();
  }

  animateFavoriteIcon() {
    this.isAnimatingFavorite = false;

    requestAnimationFrame(() => {
      this.isAnimatingFavorite = true;

      setTimeout(() => {
        this.isAnimatingFavorite = false;
      }, 300);
    });
  }

  subscribeToFavoriteToggle() {
    this.favoriteToggle$
      .pipe(debounceTime(400), takeUntil(this.destroy$))
      .subscribe(() => {
        this.favoriteButtonClick.emit();
      });
  }

  loadEventMapImage(event_thumb: string) {
    this.eventMapImage =
      event_thumb && event_thumb !== "FALTA IMPLEMENTAR"
        ? this.resolveEventImageSource(event_thumb)
        : "";
  }

  loadEventImage(event_thumb: string) {
    if (event_thumb !== "" && event_thumb !== "FALTA IMPLEMENTAR") {
      this.eventImage = this.resolveEventImageSource(event_thumb);
    }
  }

  private resolveEventImageSource(source: string): string {
    return /^(https?:|data:|blob:|\/)/i.test(source)
      ? source
      : `${environment.BLOB_URL}/${source}`;
  }

  private verifyEventInfoChanges(changes: SimpleChanges) {
    if (!this.eventInfo) {
      return;
    }

    const hasChanges = hasInputChanged<EventDetailsComponent, "eventInfo">(
      changes,
      "eventInfo",
    );
    if (hasChanges) {
      const previousEvent = changes["eventInfo"]
        .previousValue as EventInfo | null;
      if (previousEvent?.id !== this.eventInfo.id) {
        this.descriptionExpanded = false;
        this.spacesExpanded = false;
        this.expandedSpaceDescriptions.clear();
        this.expandedHalfPriceRules.clear();
        this.eventMapDialogRef?.close();
      }
      this.loadEventImage(this.eventInfo.thumbnail);
      this.loadEventMapImage(this.eventInfo.additional_images?.[0] ?? "");
    }
  }

  private async loadGoogleMaps() {
    const apiKey = environment.GOOGLE_API_KEY_MAPS;

    await this.mapsLoader.load(apiKey).then(() => (this.isGoogleLoaded = true));
  }
}
